import { useState, useEffect, useRef, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { usePermissions } from "@/context/PermissionContext";
import {
  exportCargoBackup, importCargoBackup, getCargoSystemBackups,
  createCargoBackupNow, restoreCargoSystemBackup, deleteCargoSystemBackup, getCargoBackupStatus,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Database, Download, Upload, RefreshCw, Trash2, HardDrive, Clock, AlertTriangle, Loader2, History,
} from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const fmtDate = (s) => {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleString("en-GB", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return s; }
};

export default function CargoBackupPage() {
  const { isMasterAdmin, loading: permLoading } = usePermissions();
  const fileRef = useRef(null);

  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [importOpen, setImportOpen] = useState(false);

  const [backups, setBackups] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [restoring, setRestoring] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [restoreTarget, setRestoreTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, s] = await Promise.all([getCargoSystemBackups(), getCargoBackupStatus()]);
      setBackups(b.data.backups || []);
      setStatus(s.data);
    } catch (e) {
      toast.error("Failed to load cargo backups");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isMasterAdmin) load();
  }, [isMasterAdmin, load]);

  if (!permLoading && !isMasterAdmin) return <Navigate to="/cargo/dashboard" />;

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await exportCargoBackup();
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `cargo_backup_${new Date().toISOString().split("T")[0]}.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(link.href);
      toast.success("Cargo backup exported");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to export cargo backup");
    } finally {
      setExporting(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith(".json")) {
      toast.error("Please select a .json cargo backup file");
      return;
    }
    setPendingFile(file);
    setImportOpen(true);
  };

  const handleImport = async () => {
    if (!pendingFile) return;
    setImporting(true);
    setImportOpen(false);
    try {
      const data = JSON.parse(await pendingFile.text());
      const res = await importCargoBackup(data);
      if (res.data.success) {
        const summary = Object.entries(res.data.imported_collections)
          .map(([k, v]) => `${k.replace("cargo_", "")}: ${v}`).join(", ");
        toast.success(`Cargo backup imported (${summary})`);
        load();
      } else {
        toast.error(`Import completed with errors: ${(res.data.errors || []).join(", ")}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to import — invalid file");
    } finally {
      setImporting(false);
      setPendingFile(null);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleCreateNow = async () => {
    setCreating(true);
    try {
      const res = await createCargoBackupNow();
      toast.success(res.data.message || "Cargo backup started");
      setTimeout(load, 4000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create backup");
    } finally {
      setCreating(false);
    }
  };

  const handleRestore = async () => {
    if (!restoreTarget) return;
    const target = restoreTarget;
    setRestoreTarget(null);
    setRestoring(target.id);
    try {
      const res = await restoreCargoSystemBackup(target.id);
      toast.success(`Cargo restored — ${res.data.total_documents_restored} documents from ${fmtDate(target.backup_date)}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to restore backup");
    } finally {
      setRestoring(null);
    }
  };

  const handleDelete = async (backup) => {
    setDeleting(backup.id);
    try {
      await deleteCargoSystemBackup(backup.id);
      toast.success("Backup deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete backup");
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="p-4 md:p-8 space-y-6" data-testid="cargo-backup-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 flex items-center gap-2">
          <Database className="text-emerald-500" /> Cargo Backup
        </h1>
        <p className="text-slate-500 text-sm mt-1">
          Export, import and manage automatic nightly backups of all Cargo data (entries, flights, services, agencies, invoices). Master Admin only.
        </p>
      </div>

      {/* Manual export / import */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm space-y-4">
        <h2 className="text-base md:text-lg font-semibold text-slate-800 flex items-center gap-2">
          <HardDrive size={18} className="text-slate-500" /> Export &amp; Import
        </h2>
        <p className="text-sm text-slate-500">
          Download a full copy of the Cargo data as a JSON file, or restore from a previously exported file.
        </p>
        <div className="flex flex-wrap gap-3">
          <Button onClick={handleExport} disabled={exporting} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="cargo-export-btn">
            {exporting ? <Loader2 className="animate-spin mr-2" size={16} /> : <Download size={16} className="mr-2" />}
            Export Cargo Data
          </Button>
          <input ref={fileRef} type="file" accept=".json" className="hidden" onChange={handleFileSelect} data-testid="cargo-import-input" />
          <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={importing} data-testid="cargo-import-btn">
            {importing ? <Loader2 className="animate-spin mr-2" size={16} /> : <Upload size={16} className="mr-2" />}
            Import from File
          </Button>
        </div>
        <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
          <AlertTriangle size={15} className="mt-0.5 shrink-0" />
          <span>Importing or restoring <b>replaces all current Cargo data</b>. Export a fresh backup first if unsure.</span>
        </div>
      </div>

      {/* Automatic backups */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h2 className="text-base md:text-lg font-semibold text-slate-800 flex items-center gap-2">
            <Clock size={18} className="text-slate-500" /> Automatic Backups
          </h2>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="cargo-backup-refresh">
              <RefreshCw size={14} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
            <Button size="sm" onClick={handleCreateNow} disabled={creating} className="bg-slate-800 hover:bg-slate-900 text-white" data-testid="cargo-create-backup-btn">
              {creating ? <Loader2 className="animate-spin mr-1.5" size={14} /> : <Database size={14} className="mr-1.5" />} Create backup now
            </Button>
          </div>
        </div>

        {status && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="rounded-lg border border-slate-200 p-3">
              <div className="text-xs text-slate-500">Scheduler</div>
              <div className={`text-sm font-semibold ${status.scheduler_running ? "text-emerald-600" : "text-red-600"}`}>
                {status.scheduler_running ? "Running" : "Stopped"}
              </div>
            </div>
            <div className="rounded-lg border border-slate-200 p-3">
              <div className="text-xs text-slate-500">Next backup</div>
              <div className="text-sm font-semibold text-slate-800">{status.next_backup_time}</div>
            </div>
            <div className="rounded-lg border border-slate-200 p-3">
              <div className="text-xs text-slate-500">Stored backups</div>
              <div className="text-sm font-semibold text-slate-800">{status.total_backups} / {status.max_backups_kept}</div>
            </div>
            <div className="rounded-lg border border-slate-200 p-3">
              <div className="text-xs text-slate-500">Latest backup</div>
              <div className="text-sm font-semibold text-slate-800">{status.latest_backup ? fmtDate(status.latest_backup.backup_date) : "—"}</div>
            </div>
          </div>
        )}

        <div className="border border-slate-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-slate-500">
                <tr className="border-b border-slate-200">
                  <th className="py-2.5 px-3">Date</th>
                  <th className="py-2.5 px-3">Type</th>
                  <th className="py-2.5 px-3">Created by</th>
                  <th className="py-2.5 px-3">Documents</th>
                  <th className="py-2.5 px-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {backups.length === 0 ? (
                  <tr><td colSpan={5} className="py-8 text-center text-slate-400" data-testid="cargo-backup-empty">No cargo backups yet.</td></tr>
                ) : backups.map((b) => (
                  <tr key={b.id} className="border-b border-slate-100 last:border-0" data-testid={`cargo-backup-row-${b.id}`}>
                    <td className="py-2.5 px-3 font-medium text-slate-800 flex items-center gap-2">
                      <History size={14} className="text-slate-400" /> {fmtDate(b.backup_date)}
                    </td>
                    <td className="py-2.5 px-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${b.backup_type === "automatic" ? "bg-blue-50 text-blue-600" : "bg-emerald-50 text-emerald-600"}`}>
                        {(b.backup_type || "manual").toUpperCase()}
                      </span>
                    </td>
                    <td className="py-2.5 px-3 text-slate-600">{b.created_by || "—"}</td>
                    <td className="py-2.5 px-3 text-slate-600">{b.statistics?.total_documents ?? "—"}</td>
                    <td className="py-2.5 px-3">
                      <div className="flex items-center justify-end gap-2">
                        <Button size="sm" variant="outline" onClick={() => setRestoreTarget(b)} disabled={restoring === b.id} data-testid={`cargo-backup-restore-${b.id}`}>
                          {restoring === b.id ? <Loader2 className="animate-spin mr-1.5" size={13} /> : <RefreshCw size={13} className="mr-1.5" />} Restore
                        </Button>
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50" onClick={() => handleDelete(b)} disabled={deleting === b.id} data-testid={`cargo-backup-delete-${b.id}`}>
                          {deleting === b.id ? <Loader2 className="animate-spin" size={14} /> : <Trash2 size={14} />}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Import confirm */}
      <AlertDialog open={importOpen} onOpenChange={(o) => { if (!o) { setImportOpen(false); setPendingFile(null); if (fileRef.current) fileRef.current.value = ""; } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Import cargo backup?</AlertDialogTitle>
            <AlertDialogDescription>
              This will <b>replace ALL current Cargo data</b> with the contents of <b>{pendingFile?.name}</b>. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cargo-import-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleImport} className="bg-red-600 hover:bg-red-700" data-testid="cargo-import-confirm">Import &amp; Replace</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Restore confirm */}
      <AlertDialog open={!!restoreTarget} onOpenChange={(o) => !o && setRestoreTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Restore this cargo backup?</AlertDialogTitle>
            <AlertDialogDescription>
              This will <b>replace ALL current Cargo data</b> with the backup from <b>{restoreTarget ? fmtDate(restoreTarget.backup_date) : ""}</b>. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cargo-restore-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRestore} className="bg-red-600 hover:bg-red-700" data-testid="cargo-restore-confirm">Restore</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
